<script setup lang="ts">
// import type { GA4Events } from "nuxt-ga4"
import { formatMoney } from "~/utils/formatters"
import { CrossSellingProps } from "./CrossSelling.props"
import { BundleProductCardProps } from "~/components/ProductPage/Utils/BundleProductCard.props"
import { mapProductsAlgolia, ProductAlgolia } from "~/utils/mapProducts"
import { addToCartPosition } from "~/utils/constants"
import { analyticsItemListTypes } from "~/utils/analytics/categories"

const { t } = useI18n()

const props = defineProps<CrossSellingProps>()
type AlgoliaHitsByProductCodesResponse = {
  results: ProductAlgolia[]
}

const algoliaUrl = computed(() => {
  if (!props.skuCodes?.length) return null
  return `/api/algolia/getHitsByProductCodes/${props.skuCodes.join(",")}`
})

const {
  data: algoliaResults,
  pending: pendingProducts,
  error
} = await useAsyncData<AlgoliaHitsByProductCodesResponse | null>(
  () => `algolia-products-${props.skuCodes?.join("-") || "empty"}`,
  async () => {
    if (!algoliaUrl.value) return null
    return $fetch(algoliaUrl.value)
  },
  {
    watch: [algoliaUrl],
    lazy: true
  }
)

watchEffect(() => {
  if (error.value) {
    console.error("Error fetching products from Algolia:", error.value)
  }
})

// Map Algolia results to product card props
const products = computed<BundleProductCardProps[]>(() => {
  if (!algoliaResults.value?.results) return []
  return mapProductsAlgolia(
    algoliaResults.value.results
  ) as BundleProductCardProps[]
})

// Bundle type is always "discount" for Match
const bundleType = "discount"
// Pricing (all products are always included)
const totalFullPrice = computed(() =>
  products.value.reduce((acc, { currentPrice }) => acc + currentPrice, 0)
)

const totalPrice = computed(() =>
  formatMoney(
    totalFullPrice.value * (1 - (props.percentageDiscount ?? 0) / 100)
  )
)

const totalOldPrice = computed(() =>
  formatMoney(
    products.value.reduce((acc, { oldPrice, currentPrice }) => {
      return acc + (oldPrice && oldPrice > 0 ? oldPrice : currentPrice)
    }, 0)
  )
)

const discountLabel = computed(() =>
  props.percentageDiscount ? `extra ${props.percentageDiscount}%` : null
)

// Add to cart
const { addToCart } = useCart()

const handleAddToCart = async () => {
  for (const [index, product] of products.value.entries()) {
    const gaItem: typeof GA4Entities["gaItem"] | undefined = useGAEntity<
      typeof GA4Entities["gaItem"]
    >(`ga_item_${product.productCode}`)

    const { sendEventAddToCart, mapProduct } = useGA4SiteEvents()
    gaItem.value = mapProduct(
      product as any,
      index,
      analyticsItemListTypes.crossSell
    )

    await addToCart(
      {
        sku_code: product.productCode,
        metadata: {
          product_image: product.productImage,
          slug: `${product.path}`,
          price: product.currentPrice,
          oldPrice: product?.oldPrice ?? 0,
          inStock: product?.inStock ?? false,
          unitType: product?.unitType ?? "",
          expirationDate: product.expirationDate ?? "",
          isDeductable: product.isDeductable ?? false,
          ga4Item: gaItem?.value ?? {},
          afterSearch: {
            queryID: (product as any)?.queryID
          }
        }
      },
      1
    )

    if (Object.keys(gaItem?.value ?? {}).length) {
      sendEventAddToCart(gaItem?.value ?? {}, {
        currentPrice: product?.currentPrice?.toString() ?? "",
        position: addToCartPosition.bundle
      })
    }
  }
}

const type = computed(() =>
  props.percentageDiscount > 0 ? "match" : "completePurchase"
)
</script>

<template>
  <div
    :class="`
      bundle
      pig
      flex flex-col
      gap-y-2
      p-4
      md:mb-8
      md:rounded-lg
      ${type === 'match' ? 'bg-yellow-30' : 'bg-tiffany-30'}
    `"
  >
    <h4 class="pig-bold md:dolphin-bold">
      {{ t(`productPage.bundle.${type}.title`) }}
    </h4>
    <p class="mouse md:beaver">
      {{ t(`productPage.bundle.${type}.description`) }}
    </p>
    <GAItem
      :id="`bundle-${bundleType}`"
      :item="products"
      :list-name="analyticsItemListTypes.crossSell"
    >
      <GAViewItemList>
        <div
          v-if="products?.length"
          :class="`
            bundle__products
            ${type === 'match' ? 'bg-yellow-10' : 'bg-tiffany-10'}
            flex flex-col
            gap-4
            overflow-auto
            rounded
            p-4
            md:flex md:flex-row
            md:justify-between
            md:px-4
            md:py-6
          `"
        >
          <GAListBoundleProducts :name="analyticsItemListTypes.crossSell">
            <template v-for="product in products" :key="product.productCode">
              <ProductPageUtilsBundleProductCard
                class="md:flex-1"
                v-bind="product"
                :type="bundleType"
                :selectable="false"
              />
            </template>
          </GAListBoundleProducts>
        </div>
      </GAViewItemList>
    </GAItem>
    <div
      class="
        bundle__controls
        flex flex-col
        space-y-4
        md:flex-row
        md:items-center
        md:justify-between
        md:space-y-0
      "
    >
      <div class="flex flex-col items-center gap-x-4 md:items-start">
        <p class="mouse md:beaver">
          {{ t(`productPage.bundle.${type}.totalPrice`) }}
        </p>
        <div>
          <div class="flex items-center gap-x-2">
            <strong class="elephant-bold">
              {{ totalPrice }}
            </strong>
            <small
              v-if="totalOldPrice"
              class="beaver text-black-80 line-through"
            >
              {{ totalOldPrice }}
            </small>
            <span
              v-if="discountLabel"
              class="
                badge-product
                snail-bold
                md:mouse-bold
                text-white
                bg-strawberry-main
                order-last
                whitespace-nowrap
                rounded
                p-1
                uppercase
                md:px-2
                lg:py-[2px]
              "
            >
              {{ discountLabel }}
            </span>
          </div>
        </div>
      </div>
      <UtilsButton
        theme="contained-yellow"
        :isDisabled="!products?.length"
        :text="t(`productPage.bundle.match.addProducts`)"
        @click="handleAddToCart"
      />
    </div>
  </div>
</template>
